/**
 * Координатная плоскость.
 *
 * Своя SVG, а не библиотека: в проекте нет ни одной, и тянуть её ради
 * четырёх кривых значит тянуть чужую тему, чужие шрифты и чужой зум.
 *
 * Масштаб по осям один — в камере одно поле `scale`. Разные масштабы дают
 * «красивый» график, который врёт про наклон: прямая `y = x` перестаёт идти
 * под 45°, а окружность становится овалом.
 */

import { useCallback, useEffect, useId, useRef } from "react";
import {
  edgeMark, gridStep, gridTicks, panPlot, toScreen, toWorld, worldBounds, zoomPlot,
  type PlotCamera, type PlotPoint, type PlotSize,
} from "../../lib/function-lab/viewport";
import { sampleCurve, toPathData } from "../../lib/function-lab/sampling";
import { curveFeatures, describeCurve, pointText, valuesAt } from "../../lib/function-lab/math";
import type { FunctionCurve, SceneCurve } from "../../lib/function-lab/model";

/** Подпись деления: столько знаков, сколько несёт сам шаг. */
function tickLabel(value: number, step: number): string {
  const decimals = Math.max(0, Math.min(6, -Math.floor(Math.log10(step))));
  const text = Math.abs(value) < step / 2 ? "0" : value.toFixed(decimals);
  return text.replace(/^-/, "−");
}

/** Насколько близко к краю подпись деления уже не помещается целиком. */
const EDGE = 20;

/** Прошлое положение активной кривой: бледный штрих сразу после фиксации. */
export type CurveTrace = { curve: FunctionCurve; slot: number };

export function GraphCanvas({
  curves, activeId, camera, size, offscreen, probeX, trace,
  onCamera, onSize, onProbe,
}: {
  curves: SceneCurve[];
  activeId: string;
  camera: PlotCamera;
  size: PlotSize;
  offscreen: PlotPoint[];
  probeX: number | null;
  trace?: CurveTrace | null;
  onCamera: (camera: PlotCamera) => void;
  onSize: (size: PlotSize) => void;
  onProbe?: (x: number | null) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const svg = useRef<SVGSVGElement>(null);
  const drag = useRef<{ id: number; x: number; y: number } | null>(null);
  const live = useRef(camera);
  live.current = camera;
  const helpId = useId();

  useEffect(() => {
    const element = box.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) onSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [onSize]);

  // Колесо слушается вручную: React вешает onWheel пассивно, а пассивный
  // обработчик не может отменить прокрутку страницы под плоскостью.
  useEffect(() => {
    const element = svg.current;
    if (!element) return;
    const wheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = element.getBoundingClientRect();
      const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? rect.height : 1);
      const anchor = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      onCamera(zoomPlot(live.current, { width: rect.width, height: rect.height }, anchor,
        Math.exp(-Math.max(-300, Math.min(300, delta)) * 0.002)));
    };
    element.addEventListener("wheel", wheel, { passive: false });
    return () => element.removeEventListener("wheel", wheel);
  }, [onCamera]);

  const local = useCallback((event: { clientX: number; clientY: number }): PlotPoint => {
    const rect = svg.current!.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }, []);

  if (size.width <= 0 || size.height <= 0) return <div className="fn-lab__plot" ref={box} />;

  const bounds = worldBounds(camera, size);
  const step = gridStep(camera.scale);
  const origin = toScreen(camera, size, { x: 0, y: 0 });
  // Ось, ушедшая за край, прижимается к краю: подписи делений иначе исчезают
  // вместе с ней, и ученик теряет счёт, где он на плоскости.
  const axisY = Math.max(16, Math.min(size.height - 18, origin.y));
  const axisX = Math.max(26, Math.min(size.width - 16, origin.x));
  const detached = Math.abs(axisY - origin.y) > 0.5 || Math.abs(axisX - origin.x) > 0.5;

  const label = curves.map((c) => describeCurve(c.curve)).join(". ");

  return <div className="fn-lab__plot" ref={box}>
    <svg ref={svg} width="100%" height="100%" tabIndex={0} role="img"
      aria-label={`Координатная плоскость. ${label || "Кривых нет."}`} aria-describedby={helpId}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        const point = local(event);
        drag.current = { id: event.pointerId, ...point };
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={(event) => {
        const point = local(event);
        if (drag.current?.id !== event.pointerId) {
          onProbe?.(toWorld(camera, size, point).x);
          return;
        }
        onCamera(panPlot(live.current, point.x - drag.current.x, point.y - drag.current.y));
        drag.current = { id: event.pointerId, ...point };
      }}
      onPointerUp={(event) => { if (drag.current?.id === event.pointerId) drag.current = null; }}
      onPointerCancel={() => { drag.current = null; }}
      onPointerLeave={() => onProbe?.(null)}
      onKeyDown={(event) => {
        const shifts: Record<string, [number, number]> = {
          ArrowLeft: [-60, 0], ArrowRight: [60, 0], ArrowUp: [0, -60], ArrowDown: [0, 60],
        };
        const shift = shifts[event.key];
        const middle = { x: size.width / 2, y: size.height / 2 };
        if (shift) { event.preventDefault(); onCamera(panPlot(camera, -shift[0], -shift[1])); }
        else if (event.key === "+" || event.key === "=") { event.preventDefault(); onCamera(zoomPlot(camera, size, middle, 1.25)); }
        else if (event.key === "-") { event.preventDefault(); onCamera(zoomPlot(camera, size, middle, 0.8)); }
      }}>
      <g className="fn-lab__grid" aria-hidden="true">
        {gridTicks(bounds.minX, bounds.maxX, step).map((x) => {
          const at = toScreen(camera, size, { x, y: 0 }).x;
          return <line key={`v${x}`} x1={at} y1={0} x2={at} y2={size.height} />;
        })}
        {gridTicks(bounds.minY, bounds.maxY, step).map((y) => {
          const at = toScreen(camera, size, { x: 0, y }).y;
          return <line key={`h${y}`} x1={0} y1={at} x2={size.width} y2={at} />;
        })}
      </g>

      <g className={`fn-lab__axes${detached ? " fn-lab__axes--detached" : ""}`} aria-hidden="true">
        <line x1={0} y1={axisY} x2={size.width} y2={axisY} />
        <line x1={axisX} y1={0} x2={axisX} y2={size.height} />
        <text className="fn-lab__axis-name" x={size.width - 8} y={axisY - 10} textAnchor="end">x</text>
        <text className="fn-lab__axis-name" x={axisX + 10} y={16}>y</text>
        {gridTicks(bounds.minX, bounds.maxX, step).map((x) => {
          const at = toScreen(camera, size, { x, y: 0 }).x;
          // У самого края подпись обрезается посередине, и «−10» читается как «10».
          if (Math.abs(x) < step / 2 || at < EDGE || at > size.width - EDGE) return null;
          return <text key={`tx${x}`} className="fn-lab__tick" x={at} y={axisY + 16} textAnchor="middle">{tickLabel(x, step)}</text>;
        })}
        {gridTicks(bounds.minY, bounds.maxY, step).map((y) => (
          Math.abs(y) < step / 2 ? null
            : <text key={`ty${y}`} className="fn-lab__tick" x={axisX - 8} y={toScreen(camera, size, { x: 0, y }).y + 4} textAnchor="end">{tickLabel(y, step)}</text>
        ))}
        <text className="fn-lab__tick" x={axisX - 8} y={axisY + 16} textAnchor="end">0</text>
      </g>

      {trace ? <g className={`fn-lab__trace fn-lab__curve--${trace.slot}`} aria-hidden="true">
        {sampleCurve(trace.curve, camera, size).map((path, index) => {
          const data = toPathData(path);
          return data === "" ? null : <path key={`t${index}`} className="fn-lab__stroke" d={data} />;
        })}
      </g> : null}

      {curves.map((entry) => {
        const features = curveFeatures(entry.curve);
        return <g key={entry.id} className={`fn-lab__curve fn-lab__curve--${entry.slot}${entry.id === activeId ? " fn-lab__curve--active" : ""}`}>
          {features.lines.map((line, index) => {
            const at = line.axis === "x"
              ? toScreen(camera, size, { x: line.at.value, y: 0 }).x
              : toScreen(camera, size, { x: 0, y: line.at.value }).y;
            if (!Number.isFinite(at)) return null;
            return <line key={`l${index}`} className={`fn-lab__helper fn-lab__helper--${line.role}`}
              x1={line.axis === "x" ? at : 0} y1={line.axis === "x" ? 0 : at}
              x2={line.axis === "x" ? at : size.width} y2={line.axis === "x" ? size.height : at} />;
          })}
          {features.segments.map((segment, index) => {
            const from = toScreen(camera, size, segment.from);
            const to = toScreen(camera, size, segment.to);
            // Подпись над серединой горизонтального отрезка и справа от вертикального.
            const vertical = Math.abs(to.x - from.x) < Math.abs(to.y - from.y);
            return <g key={`s${index}`} className="fn-lab__key fn-lab__segment">
              <line className="fn-lab__helper fn-lab__helper--segment"
                x1={from.x} y1={from.y} x2={to.x} y2={to.y} />
              <text x={(from.x + to.x) / 2 + (vertical ? 8 : 0)} y={(from.y + to.y) / 2 + (vertical ? 4 : -8)}
                textAnchor={vertical ? "start" : "middle"}>{segment.label}</text>
            </g>;
          })}
          {sampleCurve(entry.curve, camera, size).map((path, index) => {
            const data = toPathData(path);
            return data === "" ? null : <path key={`p${index}`} className="fn-lab__stroke" d={data} />;
          })}
          {features.points.map((point, index) => {
            const at = toScreen(camera, size, { x: point.x.value, y: point.y.value });
            if (!Number.isFinite(at.x) || !Number.isFinite(at.y)) return null;
            return <g key={`k${index}`} className="fn-lab__key">
              <circle cx={at.x} cy={at.y} r={4.5} />
              <text x={at.x + 9} y={at.y - 9}>{pointText(point)}</text>
            </g>;
          })}
        </g>;
      })}

      {probeX !== null ? <g className="fn-lab__probe" aria-hidden="true">
        <line x1={toScreen(camera, size, { x: probeX, y: 0 }).x} y1={0}
          x2={toScreen(camera, size, { x: probeX, y: 0 }).x} y2={size.height} />
        {curves.flatMap((entry) => valuesAt(entry.curve, probeX).map((y, index) => {
          if (!Number.isFinite(y)) return null;
          const at = toScreen(camera, size, { x: probeX, y });
          if (at.y < -20 || at.y > size.height + 20) return null;
          return <circle key={`${entry.id}-${index}`} className={`fn-lab__probe-dot fn-lab__curve--${entry.slot}`}
            cx={at.x} cy={at.y} r={5} />;
        }))}
      </g> : null}

      {offscreen.map((point, index) => {
        const mark = edgeMark(camera, size, point);
        return <g key={`e${index}`} className="fn-lab__edge" aria-hidden="true"
          transform={`translate(${mark.x} ${mark.y}) rotate(${(Math.atan2(mark.dy, mark.dx) * 180) / Math.PI})`}>
          <path d="M-7,-6 L2,0 L-7,6 Z" />
        </g>;
      })}
    </svg>
    <span id={helpId} className="fn-lab__sr">
      Плоскость перетаскивается мышью, масштаб меняется колёсиком. На клавиатуре: стрелки — сдвиг, плюс и минус — масштаб. Те же значения есть в таблице под графиком.
    </span>
  </div>;
}
